import styled from '@emotion/styled'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import TabsLayoutBase, * as TabsLayout from '@segment/matcha/recipes/TabsLayout'
import useTabs from '@segment/matcha/recipes/Tabs/useTabs'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import DebugYourDataImage from './DebugYourDataImage'
import ReplayDataImage from './ReplayDataImage'
import ObserveYourDataImage from './ObserveYourDataImage'

const copy = {
  heading: `Data governance and controls`,
  description: `We’ve built all the tools you need to manage your customer data. `,
}

const tabs = [
  {
    key: `debug-your-data`,
    title: `Debug`,
    description: `See a live stream of your event data flowing from your app to Segment’s API.`,
    image: <DebugYourDataImage />,
    link: {
      text: `Explore Debugger`,
      url: `/docs/connections/test-connections/`,
    },
  },
  {
    key: `replay-your-data`,
    title: `Replay`,
    description: `Replay your existing data to try new tools faster and eliminate vendor lock-in.`,
    image: <ReplayDataImage />,
    link: {
      text: `Explore Replay`,
      url: `/docs/guides/what-is-replay/`,
    },
  },
  {
    key: `observe-your-data`,
    title: `Observe`,
    description: `Easily identify and resolve data delivery issues for a comprehensive, transparent, and debuggable event pipeline you can trust.`,
    image: <ObserveYourDataImage />,
  },
]

export const DataGovernanceAndControls = () => {
  const pageConfig = usePageConfig()
  const { getTrackProps, getControlProps, getPanelProps, activeTabKey } = useTabs(
    0,
    tabs.length,
    `data-governance-and-controls`
  )

  return (
    <StyledSectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description tag="p" variant="pLarge">
            {copy.description}
          </CopyText.Description>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <TabsLayoutBase>
          <TabsLayout.Controls {...getTrackProps()}>
            {tabs.map((tab, index) => (
              <TabsLayout.Control key={tab.key} {...getControlProps(index)}>
                {tab.title}
              </TabsLayout.Control>
            ))}
          </TabsLayout.Controls>

          {tabs.map((tab, index) => (
            <TabsLayout.Content {...getPanelProps(index)} active={index === activeTabKey} key={tab.key}>
              <TabsLayout.Copy>
                <CopyTextBaseWrapper>
                  <CopyText.Heading>{tab.title}</CopyText.Heading>
                  <CopyText.Description>{tab.description}</CopyText.Description>
                  {tab.link && (
                    <CopyText.Link href={getAbsoluteUrl(tab.link.url, pageConfig)}>{tab.link.text}</CopyText.Link>
                  )}
                </CopyTextBaseWrapper>
              </TabsLayout.Copy>

              <TabsLayout.Media>{tab.image}</TabsLayout.Media>
            </TabsLayout.Content>
          ))}
        </TabsLayoutBase>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  overflow-x: visible;
  justify-content: center;
  & > ${Section.Wrapper} {
    padding-bottom: 120px;
    row-gap: 64px;
  }
`

const CopyTextBaseWrapper = styled(CopyTextBase)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    grid-area: 2 / 1 / 3 / 2;
  }
`

export default DataGovernanceAndControls
